import React, { useState } from 'react'
import { IconChevronRight } from '@tabler/icons-react'
import FolderButton from '../atoms/FolderButton'
import { api } from '../../api/root'
import { DataRoomFolder } from '../../types/projects'
import { cn } from '../../enviroment/lib/utils'
import { isInsideFolder } from '../../enviroment/utils/isInsideFolder'

interface FolderTreeNodeProps {
  projectId: string
  folder: DataRoomFolder
  depth: number
  selectedFolderPath: string | null
  onSelectFolder: (path: string) => void
  onRenameFolder: (folder: DataRoomFolder, newName: string) => Promise<void>
  onDeleteFolder: (folder: DataRoomFolder) => void
  actionsEnabled: boolean
}

const FolderTreeNode: React.FC<FolderTreeNodeProps> = ({
  projectId,
  folder,
  depth,
  selectedFolderPath,
  onSelectFolder,
  onRenameFolder,
  onDeleteFolder,
  actionsEnabled,
}) => {
  // An ancestor of the open folder starts expanded so the selection stays visible
  const [isExpanded, setIsExpanded] = useState(
    () => selectedFolderPath !== folder.path && isInsideFolder(selectedFolderPath, folder.path),
  )
  const { data, isFetching } = api.project.dataRoom.folders.useQuery(projectId, folder.path, undefined, {
    enabled: isExpanded,
  })
  const children = data?.data
  // A background refetch over cached children needs no spinner
  const isLoadingChildren = isExpanded && isFetching && children === undefined

  return (
    <>
      <div className="flex items-center" style={{ paddingLeft: depth * 12 }}>
        <button
          type="button"
          onClick={() => setIsExpanded((prev) => !prev)}
          aria-label={isExpanded ? `Collapse ${folder.name}` : `Expand ${folder.name}`}
          aria-expanded={isExpanded}
          className="flex w-5 shrink-0 items-center justify-center self-stretch rounded text-gray-400 transition-colors hover:text-gray-50"
        >
          <IconChevronRight className={cn('h-3.5 w-3.5 transition-transform', isExpanded && 'rotate-90')} />
        </button>
        <div className="min-w-0 flex-1">
          <FolderButton
            label={folder.name}
            count={folder.documentCount}
            isLoading={isLoadingChildren}
            isSelected={selectedFolderPath === folder.path}
            onClick={() => onSelectFolder(folder.path)}
            onDeleteClick={actionsEnabled ? () => onDeleteFolder(folder) : undefined}
            onRenameSubmit={actionsEnabled ? (newName) => onRenameFolder(folder, newName) : undefined}
          />
        </div>
      </div>

      {isExpanded &&
        children?.map((child) => (
          <FolderTreeNode
            key={child.id}
            projectId={projectId}
            folder={child}
            depth={depth + 1}
            selectedFolderPath={selectedFolderPath}
            onSelectFolder={onSelectFolder}
            onRenameFolder={onRenameFolder}
            onDeleteFolder={onDeleteFolder}
            actionsEnabled={actionsEnabled}
          />
        ))}
    </>
  )
}

export default FolderTreeNode
